import React, { useState } from 'react'
import { observer } from 'mobx-react-lite'
import {
  MDBIcon,
  MDBSideNav,
  MDBSideNavCollapse,
  MDBSideNavItem,
  MDBSideNavLink,
  MDBSideNavMenu,
} from 'mdb-react-ui-kit'
import NavigationStore from './Navigation.store'
import UserProfileService from './../../shared/services/UserProfile.service'
import {
  canAccessLink,
  canAccessChildrenLinks,
  canReadonlyLink,
  canReadonlyChildrenLinks,
  isLinkDisabled,
  isLinkLocked,
} from './../../shared/utilities/linkAccessFilter.function'
import CryptoJS from 'crypto-js'
import { isWhiteLabel } from './../../shared/utilities/whiteLabel.function'
import './AgentNavLinks.scss'
import Portal from './Portal.component'
import EnrollmentLinkManager from './EnrollmentLinkManager.component'

// UAD-3667: white-label downline agents only get the Contracting menu.
const WHITE_LABEL_ALLOWED_URLS = ['/agent-contracting']

const WHITE_LABEL_ALLOWED_LABELS = ['Agent Enrollment', 'My Docs']

const isWhiteLabelAllowedChild = link =>
  WHITE_LABEL_ALLOWED_LABELS.indexOf(`${link?.label}`) > -1 && canAccessLink(link)

const isWhiteLabelAllowed = link =>
  WHITE_LABEL_ALLOWED_URLS.indexOf(`${getUrl(link)}`) > -1 ||
  (Array.isArray(link?.children) && link.children.some(isWhiteLabelAllowedChild))

const COLORS = ['orange', 'yellow', 'green', 'blue', 'indigo', 'purple', 'red']

const getColor = idx => {
  return COLORS[idx % COLORS.length]
}

export const getUrl = link => {
  return link?.url
    ? /^(http:|https:)/.test(link.url)
      ? link.url
      : link.url
          .replace(':userId', UserProfileService.get('u_login'))
          .replace(
            ':recruiterId',
            CryptoJS.AES.encrypt(UserProfileService.getUserId(true).toString(), 'usabg').toString()
          )
    : ''
}

const hasChildren = link => (Array.isArray(link?.children) ? link.children : []).length > 0

const MinorNavLink = ({ link, goTo }) => {
  const [showModal, setShowModal] = useState(false)
  const toggleModal = () => setShowModal(!showModal)
  const url = getUrl(link),
    isReadonly = canReadonlyLink(link),
    canAccess = isReadonly ? isReadonly : canAccessLink(link),
    canOverride = !canAccess ? canAccessLink(link, true) : false,
    isLocked = isLinkLocked(link),
    onClickhandler = link.onClick

  return (
    <>
      <li
        className={
          'minorNavLink ' +
          (isReadonly || canAccess ? '' : 'is-hidden-link ') +
          (canOverride ? 'is-overriden-link ' : '') +
          (isLocked ? 'is-locked-link ' : '')
        }
      >
        <a
          target='_BLANK'
          rel='noopener noreferrer'
          href={!isLocked && url}
          onClick={async event => {
            event.preventDefault()
            if (!isLinkLocked(link) && onClickhandler === 'enrollmentHandler') {
              toggleModal()
            } else if (!isLinkLocked(link)) {
              if (goTo) goTo(url)
            } else {
              const lockedLink = await NavigationStore.onLockedClick(link)
              if (lockedLink && goTo && typeof lockedLink === 'string') {
                goTo(lockedLink)
              }
            }

            return false
          }}
          style={{ color: '#FFF !important' }}
        >
          <MDBIcon icon='lock' />
          {link.label}
        </a>
      </li>
      {/* Modal for enrollment link */}
      {showModal && (
        <Portal>
          <EnrollmentLinkManager showModal={showModal} toggleModal={toggleModal} />
        </Portal>
      )}
    </>
  )
}

const MinorNavDropdown = ({ link, goTo }) => {
  const isReadonly = canReadonlyChildrenLinks(link)
  const canAccess = isReadonly ? isReadonly : canAccessChildrenLinks(link)
  const canOverride = !canAccess ? canAccessChildrenLinks(link, true) : false
  const [isOpen, setIsOpen] = useState(false)

  if (!isReadonly && !canAccess) return null

  return (
    <MDBSideNavItem
      className={
        (isReadonly || canAccess ? '' : 'is-hidden-dropdown ') +
        (canOverride ? 'is-overriden-dropdown ' : '') +
        ' minorNavDropdown minorNavLink'
      }
    >
      <MDBSideNavLink
        id={`nav-sub-opt---${link?.label ? link.label.replace(/[^a-zA-Z0-9-]/g, '').toLowerCase() : 'void'}`}
        icon={isOpen ? 'angle-down' : 'angle-up'}
        onClick={() => setIsOpen(!isOpen)}
        shouldBeExpanded={true}
        className={`bg--gray theme--btn ${isOpen ? 'is-open' : ''}`}
      >
        <MDBIcon icon='lock' className='mr-2' />
        {link?.label}
      </MDBSideNavLink>

      <MDBSideNavCollapse open={isOpen}>
        {(Array.isArray(link?.children) ? link.children : []).map((child, idx) => (
          <MinorNavLinks key={`m-c-${idx}`} link={child} goTo={goTo} />
        ))}
      </MDBSideNavCollapse>
    </MDBSideNavItem>
  )
}

const MinorNavLinks = ({ link, goTo }) => {
  // Show Minor nav link w/ dropdown for further-nested children links.
  if (hasChildren(link)) return <MinorNavDropdown link={link} goTo={goTo} />

  // Show Minor nav link.
  if (`${getUrl(link)}`.length > 0) return <MinorNavLink link={link} goTo={goTo} />
}

const MajorNavDropdown = ({ link, goTo, color }) => {
  const isReadonly = canReadonlyChildrenLinks(link),
    canAccess = isReadonly ? isReadonly : canAccessChildrenLinks(link),
    canOverride = !canAccess ? canAccessChildrenLinks(link, true) : false

  const [isOpen, setIsOpen] = useState(false)

  return (
    <MDBSideNavItem
      className={
        (isReadonly || canAccess ? '' : 'is-hidden-dropdown ') +
        (canOverride ? 'is-overriden-dropdown ' : '') +
        'majorNavDropdown '
      }
    >
      <MDBSideNavLink
        id={`nav-opt---${link?.label ? link.label.replace(/[^a-zA-Z0-9-]/g, '').toLowerCase() : 'void'}`}
        icon={isOpen ? 'angle-down' : 'angle-up'}
        key={`${link?.label}`}
        onClick={() => setIsOpen(!isOpen)}
        shouldBeExpanded={true}
        className={`bg--${color} theme--btn text--uppercase`}
      >
        <MDBIcon icon='lock' className='mr-2' />
        {link?.label}
      </MDBSideNavLink>
      <MDBSideNavCollapse open={isOpen}>
        {(Array.isArray(link?.children) ? link.children : [])
          .filter(link => !isLinkDisabled(link))
          .filter(child => !isWhiteLabel() || isWhiteLabelAllowedChild(child))
          .map((child, idx) => (
            <MinorNavLinks key={`m-b-${idx}`} link={child} goTo={goTo} />
          ))}
      </MDBSideNavCollapse>
    </MDBSideNavItem>
  )
}

const MajorNavButton = ({ link, url, goTo, color }) => {
  const isReadonly = canReadonlyLink(link),
    canAccess = isReadonly ? isReadonly : canAccessLink(link),
    canOverride = !canAccess ? canAccessLink(link, true) : false

  return (
    <li
      className={
        (isReadonly || canAccess ? '' : 'is-hidden-link ') +
        (canOverride ? 'is-overriden-link ' : '') +
        ' majorNavButton '
      }
    >
      <a
        href={url}
        onClick={event => {
          event.preventDefault()
          goTo(url)
          return false
        }}
        className={`bg--${color} theme--btn text--uppercase`}
      >
        <MDBIcon icon='lock' />
        {link.label}
      </a>
    </li>
  )
}

const AgentNavLinks = ({ goTo }) => {
  const [isBuilt, setIsBuilt] = React.useState(false)

  React.useEffect(() => {
    if (!isBuilt) {
      NavigationStore.build()
      setIsBuilt(true)
    }
  }, [isBuilt])

  const renderMajorLink = (link, idx) => {
    // Show Major Nav Button w/ Dropdown for nested children links.
    if (hasChildren(link)) return <MajorNavDropdown key={`i-a-${idx}`} link={link} goTo={goTo} color={getColor(idx)} />

    // Show Major Nav Link as Button (no Dropdown).
    if (`${getUrl(link)}`.length > 0)
      return <MajorNavButton key={`m-a-${idx}`} link={link} goTo={goTo} url={getUrl(link)} color={getColor(idx)} />
  }

  if (!isBuilt) {
    return <ul id='AgentNavLinksComponent'></ul>
  }

  return (
    <ul id='AgentNavLinksComponent'>
      {NavigationStore.major
        .filter(link => !isLinkDisabled(link))
        .filter(link => !isWhiteLabel() || isWhiteLabelAllowed(link))
        .map(renderMajorLink)}
    </ul>
  )
}

export default observer(AgentNavLinks)
